import { t } from 'i18next';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useAuthUser } from 'auth/hooks';
import { useResolvePartFinderFamilyPart } from 'companySettings/hooks';
import { IPartFinderFamilyPart } from 'companySettings/types';
import { Form, FormField } from 'core/components';
import { ApiStatus, InputType, NotificationPosition } from 'core/enums';
import { FormProvider, useFormContext, useNotificationContext } from 'core/states';
import { getEmptyErrorMessage } from 'core/yup';
import { AdvancedPartNumberFormField, ManufacturerFormField } from 'parts/elements';
import { IPart, IPartManufacturer } from 'parts/types';
import { partToManufacturer } from 'parts/utils';

const DEFAULT_VALUES = { part: null, manufacturer: null, description: '' };

interface IAddPartNumberForm {
  description: string;
  manufacturer: IPartManufacturer | null;
  part: IPart | null;
}

// A part has to name its manufacturer: with its number, that is what the family keeps.
const addPartNumberSchema = () =>
  yup.object().shape({
    part: yup
      .object()
      .label(t('labelPartNumber'))
      .nullable()
      .required(getEmptyErrorMessage(t('labelPartNumber')))
      .test(
        'part-has-manufacturer',
        () => t('errorFamilyPartNoManufacturer'),
        (part) => Boolean(part?.manufacturerId)
      ),
    manufacturer: yup.object().nullable(),
    description: yup.string().nullable(),
  });

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  // false when the family has the part already: the drawer stays open and says so.
  onAdded: (part: IPartFinderFamilyPart) => boolean;
  // 1 over the wizard's panel, 0 on the family editor's page.
  level?: number;
}

const AddPartNumberFields = ({ isOpen, onClose, onAdded, level = 1 }: IProps) => {
  const { t } = useTranslation();

  const { user } = useAuthUser();
  const { setValue, watch } = useFormContext();
  const { clearNotification, showNotification } = useNotificationContext();
  const { resolvePartFinderFamilyPart } = useResolvePartFinderFamilyPart();

  const isAlternateVersionEnabled = Boolean(user?.alternateVersionEnabled);
  const hasPart = Boolean(watch('part'));

  // The manufacturer and the description come with the part: the manufacturer field narrows the
  // search until a part is picked, then shows the part's own, which is what the family keeps -
  // to change it, clear the part.
  const onChangePartNumber = (part: IPart | null) => {
    setValue('part', part, { shouldValidate: true });
    setValue('manufacturer', part ? partToManufacturer(part) : null);
    setValue('description', part?.descriptionLong || part?.description || '');
  };

  const handleOnClose = (reset: UseFormReset<IAddPartNumberForm>) => {
    clearNotification();
    onClose();
    reset(DEFAULT_VALUES);
  };

  // The part's own number and manufacturer, a SKU part's or a Partfiniti part's alike; the server
  // answers with the part as the family will list it.
  const onSubmit =
    (reset: UseFormReset<IAddPartNumberForm>) => async (data: IAddPartNumberForm) => {
      const { success, part } = await resolvePartFinderFamilyPart({
        number: data.part.number,
        manufacturerId: data.part.manufacturerId,
      });

      if (!success) {
        return;
      }

      if (!onAdded(part)) {
        showNotification({
          title: t('errorTitlePartFinderFamilyPartResolve'),
          message: t('errorPartFinderFamilyPartAlreadyAdded', { number: part.partNumber }),
          type: ApiStatus.ERROR,
          position: NotificationPosition.FORM,
        });

        return;
      }

      handleOnClose(reset);
    };

  return (
    <Form
      title={t('titleAddPartNumber')}
      isOpen={isOpen}
      onClose={handleOnClose}
      onSubmit={onSubmit}
      submitBtnLabel={t('buttonLabelAddPartNumber')}
      level={level}
    >
      <AdvancedPartNumberFormField
        onChangePartNumber={onChangePartNumber}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        hasCreateOption={false}
        level={level + 1}
      />

      <ManufacturerFormField
        name="manufacturer"
        disabled={hasPart}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        hasCreateOption={false}
      />

      <FormField
        name="description"
        type={InputType.TEXTAREA}
        inputProps={{ label: t('labelDescription'), maxRows: 6, disabled: true }}
      />
    </Form>
  );
};

// Picks a part from the app's part search - a company SKU part or a Partfiniti part - for the
// family's parts grid. Nothing is saved until the family is.
export const AddPartNumberDrawer = (props: IProps) => (
  <FormProvider schema={addPartNumberSchema()} defaultValues={DEFAULT_VALUES} resetDefaultValues>
    <AddPartNumberFields {...props} />
  </FormProvider>
);
